import { useEffect, useState } from "react";
import { BookOpen, RefreshCw } from "lucide-react";
import { Shell } from "@/components/Shell";
import { api, errMsg } from "@/lib/api";

export default function TradingLogic() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/trading-logic");
      setData(data);
      setErr("");
    } catch (e) {
      setErr(errMsg(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <Shell>
      <div className="max-w-[1000px] mx-auto px-4 sm:px-6 py-8 space-y-8" data-testid="trading-logic-page">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight">Trading logic</h1>
            <p className="mt-2 text-sm text-zinc-400 max-w-xl leading-relaxed">
              Exactly what the system does to a trade, in plain English. Written from the live
              settings, so it updates itself whenever you change a rule.
            </p>
          </div>
          <div className="flex-1" />
          <button
            onClick={load}
            data-testid="trading-logic-refresh"
            className="inline-flex items-center gap-2 px-3 py-2 border border-zinc-800 hover:border-emerald-600 text-[10px] uppercase tracking-[0.12em] font-semibold text-zinc-400 hover:text-emerald-400 rounded-sm transition-colors duration-200"
          >
            <RefreshCw className="h-3 w-3" /> Refresh
          </button>
        </div>

        {err && (
          <p className="font-mono text-xs text-red-400" data-testid="trading-logic-error">
            {err}
          </p>
        )}

        {data && (
          <>
            <p className="font-mono text-[11px] text-zinc-600" data-testid="trading-logic-stamp">
              Generated {data.generated_at} · global defaults shown; a trader's own overrides win
            </p>

            <div className="space-y-6">
              {data.sections.map((sec, i) => (
                <section
                  key={sec.title}
                  className="border border-zinc-800 bg-zinc-950/60 rounded-sm p-4 sm:p-6"
                  data-testid={`trading-logic-section-${i}`}
                >
                  <h2 className="flex items-center gap-2 text-base md:text-lg text-zinc-100">
                    <BookOpen className="h-4 w-4 text-emerald-400" />
                    {sec.title}
                  </h2>
                  <ul className="mt-4 space-y-2.5">
                    {sec.bullets.map((b) => (
                      <li key={b} className="flex gap-3 text-sm text-zinc-300 leading-relaxed">
                        <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-emerald-500" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>

            <div className="grid gap-4 sm:grid-cols-2" data-testid="trading-logic-scope">
              <ScopeCard
                title="Set per trader"
                note="Defaults come from Admin settings; override any of these on a trader's page."
                items={data.scope.per_trader}
                tone="emerald"
              />
              <ScopeCard
                title="Global only"
                note="Applies to everyone on the desk at once."
                items={data.scope.global_only}
                tone="amber"
              />
            </div>
          </>
        )}
      </div>
    </Shell>
  );
}

const ScopeCard = ({ title, note, items, tone }) => (
  <div className="border border-zinc-800 bg-zinc-950/60 rounded-sm p-4 sm:p-5">
    <p
      className={`text-[10px] uppercase tracking-[0.16em] font-semibold ${
        tone === "emerald" ? "text-emerald-400" : "text-amber-500"
      }`}
    >
      {title}
    </p>
    <p className="mt-1.5 text-xs text-zinc-500 leading-relaxed">{note}</p>
    <ul className="mt-3 space-y-1.5">
      {items.map((i) => (
        <li key={i} className="font-mono text-[11px] text-zinc-400">
          · {i}
        </li>
      ))}
    </ul>
  </div>
);
